import { TranslocoService } from '@jsverse/transloco';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FuseConfirmationConfig } from '@fuse/services/confirmation/confirmation.types';
import { FuseConfirmationDialogComponent } from '@fuse/services/confirmation/dialog/dialog.component';
import { ErrorDialogPort } from 'app/core/errors/error.tokens';
import { NormalizedError } from 'app/core/errors/error.types';

export class ErrorDialogAdapter implements ErrorDialogPort {
    private readonly transloco = inject(TranslocoService);
    private readonly dialog = inject(MatDialog);

    open(error: NormalizedError): void {
        const html = buildHtml(
            error,
            this.transloco.translate('errors.occurred'),
            this.transloco.translate('errors.details')
        );

        const config: FuseConfirmationConfig = {
            title: this.transloco.translate('errors.dialogTitle'),
            message: html, // HTML erlaubt (ohne <style>)
            icon: { show: true, name: 'heroicons_outline:exclamation-triangle', color: 'error' },
            actions: {
                confirm: { show: true, label: 'OK', color: 'primary' },
                cancel: { show: false },
            },
            dismissible: true,
        };

        this.dialog.open(FuseConfirmationDialogComponent, {
            data: config,
            autoFocus: false,
            disableClose: !config.dismissible,
            // <— unsere Panel-Klasse, damit globales CSS greifen kann
            panelClass: ['fuse-confirmation-dialog-panel', 'error-dialog-panel'],
            // kein inline max-width, damit unsere CSS-Breite wirken kann (optional)
            maxWidth: 'none',
            restoreFocus: true,
        });
    }
}

function buildHtml(err: NormalizedError, fallbackMessage: string, detailsLabel: string): string {
    const esc = (s: string) => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const msg = esc(err.message ?? fallbackMessage);

    const meta: string[] = [];
    if (err.status != null) meta.push(`Status: ${err.status} • `);
    if (err.code != null)   meta.push(`Code: ${err.code}`);
    if (err.traceId)        meta.push(`<br><span class="fe-traceid">Trace-ID: ${esc(err.traceId)}</span>`);

    const metaLine = meta.length ? `<div class="fe-meta">${meta.join('')}</div>` : '';

    const stack = err.stack?.trim();
    const raw   = err.raw ? esc(JSON.stringify(err.raw, null, 2)) : '';
    const detailsBlock = (stack || raw)
        ? `
      <details class="fe-details">
        <summary class="fe-summary" style="cursor:pointer;user-select:none;">${esc(detailsLabel)}</summary>
        <pre class="fe-trace">${esc(stack || '') || raw}</pre>
      </details>
    `
        : '';

    return `
    <div class="fe-root">
      <div class="fe-msg">${msg}</div>
      ${metaLine}
      ${detailsBlock}
    </div>
  `;
}

